// src/components/welcome/WelcomeExperience.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRight, X, User, Users, Heart, Trophy, BarChart2,
  ChevronRight, ChevronLeft, AlertTriangle, CheckCircle,
} from 'lucide-react';
import LoadingScreen from './LoadingScreen';
import { useLeagueData, useManagerContext } from '../../context/DataContext';
import FPLLogo from '../shared/FPLLogo';

// ── Helpers ───────────────────────────────────────────────────────────────────

const isValidLeagueIdFormat = (val) => /^\d{5,8}$/.test(val.trim());

const getOrCreateClientId = () => {
  try {
    let id = sessionStorage.getItem('fpl_client_id');
    if (!id) {
      id = crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('fpl_client_id', id);
    }
    return id;
  } catch {
    return 'anon';
  }
};


// ── Funny captions — shown on journey selection while data loads ──────────────

const CAPTIONS = [
  "Rewinding your transfer regrets…",
  "Calculating all those points left on the bench…",
  "Finding your biggest captain fails…",
  "Locating your differential masterstrokes…",
  "Calculating how many times you cursed Erling Haaland…",
  "Finding all the players who blanked as soon as you bought them…",
  "Remembering that triple captain you wasted…",
  "Measuring your rank drops after wildcards…",
  "Counting how many times you forgot to set your team…",
  "Analysing your 2am transfer decisions…",
  "Remembering those hits that didn't pay off…",
  "Calculating how many times you said 'never again'…",
  "Preparing your FPL journey…",
];

// ── Pseudo progress bar (time-based, never looks stuck) ──────────────────────
//
// Runs from 0 → ~90% over TOTAL_MS, then snaps to 100% when done=true.
// Uses an ease-out curve so it moves fast at first and slows near the cap.

const TOTAL_MS = 45_000; // expected max seed + analysis time

const PseudoProgressBar = ({ done, failed, failedReason }) => {
  const [pct,          setPct]          = useState(0);
  const [captionIdx,   setCaptionIdx]   = useState(0);
  const startRef = useRef(Date.now());

  // Time-based progress
  useEffect(() => {
    if (done)   { setPct(100); return; }
    if (failed) { return; }
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const ratio   = Math.min(elapsed / TOTAL_MS, 1);
      const eased   = 1 - Math.pow(1 - ratio, 1.8);
      setPct(Math.round(Math.min(eased * 92, 92)));
      if (!done && !failed && ratio < 1) requestAnimationFrame(tick);
    };
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [done, failed]);

  // Caption cycling — 2.5s per caption
  useEffect(() => {
    if (done || failed) return;
    const id = setInterval(() => {
      setCaptionIdx(i => (i + 1) % CAPTIONS.length);
    }, 2500);
    return () => clearInterval(id);
  }, [done, failed]);

  if (failed) {
    return (
      <div className="text-xs text-center space-y-2">
        <p style={{ color: 'var(--c-bad)' }}>
          {failedReason || 'Something went wrong — please refresh and try again.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '6px 16px', borderRadius: '6px', fontSize: '12px',
            backgroundColor: 'var(--c-surface-3)',
            border: '1px solid var(--c-border)',
            color: 'var(--c-grey)', cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span style={{ color: 'var(--c-turquoise)' }}>
          {done ? 'Ready!' : CAPTIONS[captionIdx]}
        </span>
        <span style={{ color: 'var(--c-grey)' }}>{pct}%</span>
      </div>
      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--c-surface-3)' }}>
        <div
          className="h-full rounded-full"
          style={{
            width:      `${pct}%`,
            transition: done ? 'width 0.4s ease-out' : 'width 0.15s linear',
            background: done
              ? 'var(--c-fpl-green)'
              : 'linear-gradient(90deg, var(--c-blue), var(--c-fpl-green))',
          }}
        />
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const WelcomeExperience = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const clientId  = useRef(getOrCreateClientId());

  const { data, loadFromAPI, startSeed, cancelSeed, pollStatus } = useLeagueData();
  const { selectManager, selectedManager } = useManagerContext();

  // ── Phase ─────────────────────────────────────────────────────────────────

  const getInitialPhase = () => {
    if (location.state?.forceSelectManager)   return 'selectManager';
    if (location.state?.forceJourneySelection) return selectedManager ? 'journeySelection' : 'selectManager';
    if (location.pathname === '/journey-selector') return selectedManager ? 'journeySelection' : 'selectManager';
    return 'landing';
  };

  const [currentPhase, setCurrentPhase] = useState(getInitialPhase);

  useEffect(() => {
    if (location.state?.forceSelectManager) {
      setCurrentPhase('selectManager');
    } else if (location.state?.forceJourneySelection) {
      setCurrentPhase(selectedManager ? 'journeySelection' : 'selectManager');
    } else if (location.pathname === '/journey-selector') {
      setCurrentPhase(selectedManager ? 'journeySelection' : 'selectManager');
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── League input state ────────────────────────────────────────────────────

  const [leagueId,      setLeagueId]      = useState('');
  const [seedStatus,    setSeedStatus]    = useState(null);
  const [isDataReady,   setIsDataReady]   = useState(!!data);
  const prevLeagueId    = useRef(null);
  const seedTriggerTime = useRef(null);

  // ── Manager list (available before data loads, from seedStatus.managers) ──

  const [managerList,   setManagerList]   = useState([]);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [managerName,   setManagerName]   = useState('');
  const [managerError,  setManagerError]  = useState('');
  const [showAll,       setShowAll]       = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);

  // Populate manager list from data once it's loaded, or from seedStatus
  useEffect(() => {
    if (data) {
      const names = Object.keys(data.performanceVsAverage?.managers || {});
      if (names.length > 0) setManagerList(names.sort());
    }
  }, [data]);

  useEffect(() => {
    if (seedStatus?.managers?.length > 0 && managerList.length === 0) {
      setManagerList(seedStatus.managers.map(m => m.manager_name).sort());
    }
  }, [seedStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived preview state ─────────────────────────────────────────────────

  const leagueName    = seedStatus?.league_name   ?? null;
  const managerCount  = seedStatus?.total_managers ?? 0;
  const tooLarge      = managerCount > 50;
  const previewReady  = !!leagueName && managerCount > 0;
  const canProceed    = previewReady && !tooLarge;

  // ── Debounced seed trigger on valid format ────────────────────────────────

  useEffect(() => {
    if (!isValidLeagueIdFormat(leagueId)) return;

    const lid = parseInt(leagueId, 10);
    const timer = setTimeout(async () => {
      // Abort previous league if different
      if (prevLeagueId.current && prevLeagueId.current !== lid) {
        cancelSeed(prevLeagueId.current);
        setSeedStatus(null);
        setManagerList([]);
      }
      prevLeagueId.current    = lid;
      seedTriggerTime.current = Date.now();
      await startSeed(lid, clientId.current);
    }, 1000);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  // ── Status polling — with consecutive-failure counter ────────────────────

  const pollFailures = useRef(0);
  const [pollError,  setPollError] = useState(null); // shown after repeated failures

  useEffect(() => {
    if (!isValidLeagueIdFormat(leagueId)) return;
    const lid = parseInt(leagueId, 10);

    const poll = async () => {
      try {
        const status = await pollStatus(lid);
        pollFailures.current = 0;
        setPollError(null);
        setSeedStatus(status);

        if (status.managers?.length > 0) {
          setManagerList(status.managers.map(m => m.manager_name).sort());
        }

        if (status.overview_ready && !isDataReady) {
          const ok = await loadFromAPI(lid);
          if (ok) setIsDataReady(true);
        }
      } catch {
        pollFailures.current += 1;
        if (pollFailures.current >= 4) {
          setPollError("Can't reach the server — check your connection.");
        }
      }
    };

    poll();
    const interval = setInterval(poll, 2000);
    return () => { clearInterval(interval); pollFailures.current = 0; setPollError(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, currentPhase, isDataReady]);

  // Also poll during selectManager and journeySelection phases
  useEffect(() => {
    if (!['selectManager', 'journeySelection'].includes(currentPhase)) return;
    if (!leagueId || isDataReady) return;

    const lid = parseInt(leagueId, 10);
    const poll = async () => {
      try {
        const status = await pollStatus(lid);
        pollFailures.current = 0;
        setPollError(null);
        setSeedStatus(status);
        if (status.overview_ready && !isDataReady) {
          const ok = await loadFromAPI(lid);
          if (ok) setIsDataReady(true);
        }
      } catch {
        pollFailures.current += 1;
        if (pollFailures.current >= 4) {
          setPollError("Can't reach the server — check your connection.");
        }
      }
    };

    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhase, isDataReady]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleLeagueIdChange = (e) => {
    const val = e.target.value.replace(/\D/g, ''); // digits only
    setLeagueId(val);
    setSeedStatus(null);
    setManagerList([]);
    setIsDataReady(false);
  };

  const handleNext = () => {
    if (!canProceed) return;
    setCurrentPhase('loading');
  };

  const handleManagerSelect = (name) => {
    selectManager(name);
    setCurrentPhase('managerTransition');
    setTimeout(() => setCurrentPhase('journeySelection'), 1200);
  };

  const handleManagerSubmit = (e) => {
    e.preventDefault();
    const match = managerList.find(n => n.toLowerCase() === managerName.toLowerCase());
    if (match) {
      handleManagerSelect(match);
    } else {
      setManagerError('Name not found. Try selecting from the list below.');
    }
  };

  const filteredManagers = searchQuery
    ? managerList.filter(m => m.toLowerCase().split(/\s+/).some(w => w.startsWith(searchQuery.toLowerCase())))
    : managerList;
  const displayedManagers = searchQuery ? filteredManagers : (showAll ? managerList : managerList.slice(0, 12));

  // ── Phase renderers ───────────────────────────────────────────────────────

  const renderLanding = () => (
    <div
      className="h-full w-full flex flex-col items-center justify-center text-white relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 60%, #0d1a2e 100%)' }}
    >
      

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-sm w-full">
        <div className="mb-8 animate-fade-in">
          <FPLLogo size="full" colour="light" className="h-7 mx-auto" />
        </div>

        <div className="mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-5xl font-bold mb-2 tracking-tight">
            FPL <span style={{ color: 'var(--c-blue)' }}>Wrapped</span>
          </h1>
          <p className="text-base" style={{ color: 'var(--c-grey)' }}>
            Your 2025/26 season in review
          </p>
        </div>

        {/* League ID input */}
        <div className="w-full mb-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <label htmlFor="league-id" className="block text-sm font-medium mb-2 text-left text-white">
            Enter your FPL League ID
          </label>
          <input
            id="league-id"
            type="text"
            inputMode="numeric"
            value={leagueId}
            onChange={handleLeagueIdChange}
            placeholder="e.g. 123456"
            className="w-full px-4 py-3 rounded-xl text-white text-sm transition-all"
            style={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: `1px solid ${tooLarge ? 'var(--c-bad)' : previewReady ? 'var(--c-fpl-green)' : 'rgba(255,255,255,0.15)'}`,
              outline: 'none',
              boxShadow: 'none',
            }}
            onFocus={(e) => { e.target.style.border = '1px solid var(--c-blue)'; e.target.style.boxShadow = '0 0 0 2px rgba(63,105,174,0.2)'; }}
            onBlur={(e)  => { e.target.style.border = `1px solid ${tooLarge ? 'var(--c-bad)' : previewReady ? 'var(--c-fpl-green)' : 'rgba(255,255,255,0.15)'}`;  e.target.style.boxShadow = 'none'; }}
          />
          <p className="text-xs mt-1.5" style={{ color: 'var(--c-grey)' }}>
            Find this in the URL when viewing your FPL league
          </p>
        </div>

        {/* League preview */}
        {isValidLeagueIdFormat(leagueId) && (
          <div className="w-full mb-4 animate-fade-in">
            {/* Show spinner while seed is in flight or status is pending/not_found within 5s */}
            {(!seedStatus || (!previewReady && seedStatus.status !== 'failed' && !(
              seedStatus.status === 'not_found' && seedTriggerTime.current && Date.now() - seedTriggerTime.current > 5000
            ))) && !tooLarge && (
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--c-grey)' }}>
                <div className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                {!seedStatus ? 'Fetching league details…' : 'Loading…'}
              </div>
            )}

            {seedStatus?.status === 'failed' && (
              <div className="flex items-start gap-2 text-xs" style={{ color: 'var(--c-bad)' }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>{seedStatus.error_reason || 'League not found. Check the ID and try again.'}</span>
              </div>
            )}

            {pollError && !seedStatus?.status && (
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--c-warning)' }}>
                <AlertTriangle size={14} />
                {pollError}
              </div>
            )}

            {previewReady && !tooLarge && (
              <div
                className="rounded-xl p-4 text-left animate-fade-in"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} style={{ color: 'var(--c-fpl-green)' }} />
                    <span className="text-sm font-semibold">{leagueName}</span>
                  </div>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'var(--c-grey)' }}
                  >
                    Classic League
                  </span>
                </div>
                <div>
                  <div className="text-3xl font-bold" style={{ color: 'var(--c-blue)' }}>{managerCount}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--c-grey)' }}>
                    {managerCount === 1 ? 'Manager' : 'Managers'}
                  </div>
                </div>
              </div>
            )}

            {tooLarge && (
              <div
                className="rounded-xl p-4 text-left animate-fade-in"
                style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={16} style={{ color: 'var(--c-bad)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--c-bad)' }}>{leagueName}</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--c-bad)' }}>
                  This league has {managerCount} managers. FPL Wrapped supports leagues with 50 or fewer managers.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Next arrow */}
        <button
          onClick={handleNext}
          disabled={!canProceed}
          className="w-full py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all animate-fade-in"
          style={{
            animationDelay: '0.3s',
            backgroundColor: canProceed ? 'var(--c-blue)' : 'var(--c-surface-3)',
            color:           canProceed ? 'white' : 'var(--c-grey)',
            cursor:          canProceed ? 'pointer' : 'not-allowed',
            boxShadow:       canProceed ? '0 4px 20px rgba(63,105,174,0.4)' : 'none',
          }}
        >
          Let's go
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  const renderManagerSelection = () => (
    <div
      className="h-full w-full flex flex-col items-center justify-center text-white p-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 100%)' }}
    >
      

      {/* Back to landing */}
      <button
        onClick={() => {
          setSeedStatus(null);
          setManagerList([]);
          setLeagueId('');
          setIsDataReady(false);
          setCurrentPhase('landing');
        }}
        className="absolute top-6 left-6 z-20 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all"
        style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--c-surface-3)'; e.currentTarget.style.color = 'white'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--c-surface-2)'; e.currentTarget.style.color = 'var(--c-grey)'; }}
      >
        <ChevronLeft size={16} />
        Change League
      </button>

      <div
        className="max-w-md w-full rounded-2xl p-8 relative z-10"
        style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
      >
        <div className="flex justify-center mb-6">
          <FPLLogo size="icon" colour="light" className="h-8" />
        </div>

        <h2 className="text-2xl font-bold mb-1 text-center">Who are you?</h2>
        <p className="text-sm text-center mb-6" style={{ color: 'var(--c-grey)' }}>
          {leagueName
            ? `Select your name from ${leagueName}`
            : 'Select your manager name to view your personal season stats'}
        </p>

        <form onSubmit={handleManagerSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={managerName}
              onChange={(e) => { setManagerName(e.target.value); setSearchQuery(e.target.value); setManagerError(''); }}
              className="w-full px-4 py-3 rounded-xl text-white text-sm focus:outline-none transition-all"
              style={{ backgroundColor: 'var(--c-surface-3)', border: '1px solid var(--c-border)' }}
              placeholder="Search manager name…"
              autoComplete="off"
              onFocus={(e) => { e.target.style.borderColor = 'var(--c-blue)'; }}
              onBlur={(e)  => { e.target.style.borderColor = 'var(--c-border)'; }}
            />
            {managerName && (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100 transition-opacity"
                onClick={() => { setManagerName(''); setSearchQuery(''); }}
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {managerError && (
            <p className="text-xs" style={{ color: 'var(--c-bad)' }}>{managerError}</p>
          )}

          {managerList.length === 0 ? (
            <div className="flex items-center gap-2 text-xs justify-center py-4" style={{ color: 'var(--c-grey)' }}>
              <div className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
              Loading manager list…
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs" style={{ color: 'var(--c-grey)' }}>
                  {searchQuery
                    ? `${filteredManagers.length} match${filteredManagers.length !== 1 ? 'es' : ''}`
                    : `${displayedManagers.length} of ${managerList.length} managers`}
                </p>
                {!searchQuery && managerList.length > 12 && (
                  <button type="button" onClick={() => setShowAll(!showAll)} className="text-xs" style={{ color: 'var(--c-blue)' }}>
                    {showAll ? 'Show less' : 'Show all'}
                  </button>
                )}
              </div>

              <div className="max-h-52 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--c-blue) var(--c-surface-3)' }}>
                <div className="grid grid-cols-2 gap-1.5">
                  {displayedManagers.map((manager, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setManagerName(manager)}
                      className="px-3 py-2 rounded-lg text-xs text-left truncate transition-all"
                      style={{
                        backgroundColor: managerName === manager ? 'rgba(63,105,174,0.3)' : 'var(--c-surface-3)',
                        border:          managerName === manager ? '1px solid var(--c-blue)' : '1px solid transparent',
                        color:           managerName === manager ? 'white' : 'var(--c-grey)',
                      }}
                    >
                      {manager}
                    </button>
                  ))}
                </div>
                {filteredManagers.length === 0 && searchQuery && (
                  <p className="text-xs text-center mt-3" style={{ color: 'var(--c-warning)' }}>
                    No matches found.
                  </p>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={!managerName}
            className="w-full py-3 rounded-xl text-sm font-medium transition-all"
            style={{
              backgroundColor: managerName ? 'var(--c-blue)' : 'var(--c-surface-3)',
              color:           managerName ? 'white' : 'var(--c-grey)',
              cursor:          managerName ? 'pointer' : 'not-allowed',
            }}
          >
            Continue
          </button>
        </form>
      </div>
    </div>
  );

  const renderJourneySelection = () => {
    const locked = !isDataReady;

    return (
      <div
        className="h-full w-full flex flex-col items-center justify-center text-white p-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 100%)' }}
      >
        

        <button
          onClick={() => setCurrentPhase('selectManager')}
          className="absolute top-6 left-6 z-20 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all"
          style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--c-surface-3)'; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--c-surface-2)'; e.currentTarget.style.color = 'var(--c-grey)'; }}
        >
          <ChevronLeft size={16} />
          Change Player
        </button>

        <div className="max-w-lg w-full relative z-10">
          <div className="text-center mb-8 animate-fade-in">
            <FPLLogo size="icon" colour="light" className="h-8 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">
              Welcome, <span style={{ color: 'var(--c-blue)' }}>{selectedManager}</span>!
            </h2>
            <p className="text-sm" style={{ color: 'var(--c-grey)' }}>
              {locked
                ? 'Your wrapped is being prepared…'
                : 'How would you like to explore your 2025/26 FPL season?'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* My Journey — blue */}
            <button
              onClick={() => !locked && navigate('/my-journey')}
              className="p-6 rounded-2xl text-left flex flex-col transition-all"
              style={{
                backgroundColor: 'var(--c-surface-2)',
                border:          '1px solid var(--c-border)',
                opacity:         locked ? 0.4 : 1,
                cursor:          locked ? 'not-allowed' : 'pointer',
                pointerEvents:   locked ? 'none' : 'auto',
                transition:      'opacity 0.4s ease, border-color 0.2s, background-color 0.2s',
              }}
              onMouseEnter={(e) => { if (!locked) { e.currentTarget.style.borderColor = 'var(--c-blue)'; e.currentTarget.style.backgroundColor = 'var(--c-surface-3)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-border)'; e.currentTarget.style.backgroundColor = 'var(--c-surface-2)'; }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(63,105,174,0.15)', border: '1px solid rgba(63,105,174,0.3)' }}>
                <User size={22} style={{ color: 'var(--c-blue)' }} />
              </div>
              <h3 className="font-bold text-lg mb-1">My Journey</h3>
              <p className="text-xs mb-4 flex-1" style={{ color: 'var(--c-grey)' }}>
                Your personal season — transfers, captains, differentials.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['Transfer Regrets', 'Captain Choices', 'Differentials'].map(tag => (
                  <span key={tag} className="c-badge c-badge-blue text-xs">{tag}</span>
                ))}
              </div>
            </button>

            {/* League Overview — turquoise */}
            <button
              onClick={() => !locked && navigate('/league-overview')}
              className="p-6 rounded-2xl text-left flex flex-col transition-all"
              style={{
                backgroundColor: 'var(--c-surface-2)',
                border:          '1px solid var(--c-border)',
                opacity:         locked ? 0.4 : 1,
                cursor:          locked ? 'not-allowed' : 'pointer',
                pointerEvents:   locked ? 'none' : 'auto',
                transition:      'opacity 0.4s ease, border-color 0.2s, background-color 0.2s',
              }}
              onMouseEnter={(e) => { if (!locked) { e.currentTarget.style.borderColor = 'var(--c-turquoise)'; e.currentTarget.style.backgroundColor = 'var(--c-surface-3)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-border)'; e.currentTarget.style.backgroundColor = 'var(--c-surface-2)'; }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(68,213,163,0.12)', border: '1px solid rgba(68,213,163,0.3)' }}>
                <Users size={22} style={{ color: 'var(--c-turquoise)' }} />
              </div>
              <h3 className="font-bold text-lg mb-1">League Overview</h3>
              <p className="text-xs mb-4 flex-1" style={{ color: 'var(--c-grey)' }}>
                Awards, rankings and how you stack up against the league.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['League Awards', 'Rankings', 'Performance Stats'].map(tag => (
                  <span key={tag} className="c-badge text-xs" style={{ backgroundColor: 'rgba(68,213,163,0.1)', color: 'var(--c-turquoise)', border: '1px solid rgba(68,213,163,0.25)' }}>{tag}</span>
                ))}
              </div>
            </button>
          </div>

          {/* Progress bar — shown while analysis is running */}
          {locked && (
            <div className="mt-6 space-y-2">
              <PseudoProgressBar
                done={isDataReady}
                failed={seedStatus?.status === 'failed'}
                failedReason={seedStatus?.error_reason}
              />
              {pollError && !isDataReady && (
                <p className="text-xs text-center" style={{ color: 'var(--c-warning)' }}>
                  {pollError}
                </p>
              )}
            </div>
          )}

          {!locked && (
            <p className="text-center text-xs mt-6" style={{ color: 'var(--c-grey)' }}>
              You can switch between views at any time using the menu
            </p>
          )}
        </div>

        {/* Sliding menu */}
        {menuOpen && (
          <div className="absolute inset-0 z-20" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <div className="p-4 flex justify-between items-center" style={{ borderBottom: '1px solid var(--c-border)' }}>
              <FPLLogo size="full" colour="light" className="h-6" />
              <button onClick={() => setMenuOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Close menu">
                <X size={20} />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {[
                { icon: <BarChart2 size={18} />, label: 'My Journey',       path: '/my-journey' },
                { icon: <Heart size={18} />,     label: 'Transfer Regrets', path: '/transfer-regrets' },
                { icon: <Trophy size={18} />,    label: 'Captain Choices',  path: '/captain-hindsight' },
                { icon: <BarChart2 size={18} />, label: 'Differentials',    path: '/differential-king' },
                { icon: <Trophy size={18} />,    label: 'League Awards',    path: '/league-awards' },
                { icon: <Users size={18} />,     label: 'League Overview',  path: '/league-overview' },
              ].map(item => (
                <button
                  key={item.path}
                  onClick={() => { setMenuOpen(false); navigate(item.path); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-left transition-all"
                  style={{ color: 'var(--c-grey)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--c-surface-2)'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--c-grey)'; }}
                >
                  <span style={{ color: 'var(--c-blue)' }}>{item.icon}</span>
                  {item.label}
                  <ChevronRight size={14} className="ml-auto opacity-40" />
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    );
  };

  const renderTransitionSpinner = () => (
    <div
      className="h-full w-full flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 100%)' }}
    >
      <div
        style={{
          width:        '36px',
          height:       '36px',
          border:       '3px solid rgba(63, 105, 174, 0.2)',
          borderTop:    '3px solid var(--c-blue)',
          borderRadius: '50%',
          animation:    'spin 0.9s linear infinite',
        }}
      />
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="w-full h-screen">
      {currentPhase === 'landing'             && renderLanding()}
      {currentPhase === 'loading'             && (
        <LoadingScreen onComplete={() => setCurrentPhase('selectManager')} />
      )}
      {currentPhase === 'selectManager'       && renderManagerSelection()}
      {currentPhase === 'managerTransition'   && renderTransitionSpinner()}
      {currentPhase === 'journeySelection'    && renderJourneySelection()}
    </div>
  );
};

export default WelcomeExperience;
